import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import VideoEditingService from 'services/VideoEditingService';

const service = new VideoEditingService();

interface Props {
  open: boolean;
  onClose: () => void;
}

// Manage the Google Drive folders whose new clips are transcribed automatically in
// the background (the reconciler sweep, ~every 15 min) — no need to open them here.
interface Entry { url: string; addedAt: number | null }

export default function AutoTranscribeDialog({ open, onClose }: Props) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setInput('');
    setLoading(true);
    service.getAutoTranscribeFolders()
      .then((resp) => {
        if (resp) setEntries(resp.folders.map((f) => ({ url: f.folderUrl, addedAt: f.addedAt })));
        else setError('Failed to load the current folders.');
      })
      .finally(() => setLoading(false));
  }, [open]);

  function addEntry() {
    const v = input.trim();
    if (!v) return;
    setEntries((cur) => (cur.some((e) => e.url === v) ? cur : [...cur, { url: v, addedAt: null }]));
    setInput('');
  }

  function removeEntry(i: number) {
    setEntries((cur) => cur.filter((_, idx) => idx !== i));
  }

  async function save() {
    setSaving(true);
    setError(null);
    const resp = await service.setAutoTranscribeFolders(entries.map((e) => e.url));
    setSaving(false);
    if (!resp) { setError('Failed to save. Try again.'); return; }
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-transcribe folders</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Videos added to these Google Drive folders (including any subfolders) are transcribed
          automatically in the background. Only videos added <b>after</b> you save a folder are
          transcribed — the existing library is left alone.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
              <TextField
                size="small"
                fullWidth
                label="Add a Drive folder link or id"
                placeholder="https://drive.google.com/drive/folders/…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
              />
              <Button variant="outlined" onClick={addEntry} disabled={!input.trim()} sx={{ mt: 0.25 }}>Add</Button>
            </Box>

            {entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No folders yet — add one above.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {entries.map((e, i) => (
                  <Box key={`${e.url}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Link
                        href={e.url.startsWith('http') ? e.url : `https://drive.google.com/drive/folders/${e.url}`}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                        sx={{ display: 'block', wordBreak: 'break-all' }}
                      >
                        {e.url}
                      </Link>
                      <Typography variant="caption" color="text.secondary">
                        {e.addedAt ? `watching new videos since ${new Date(e.addedAt).toLocaleDateString()}` : 'new — watches for videos added after you save'}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => removeEntry(i)} aria-label="Remove folder">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || loading} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
